import React, { useCallback, useEffect, useState } from "react";
import styled from "@emotion/styled";
import { toast } from "react-toastify";
import Text from "@components/Text";
import Button from "@components/Button";
import Input from "@components/Input";
import Spinner from "@components/Spinner";
import {
  AdminBanner,
  getNotificationBanners,
  createNotificationBanner,
  updateNotificationBanner,
  deleteNotificationBanner,
} from "@services/adminService";
import {
  BannerSeverity,
  SEVERITY_STYLES,
} from "@services/notificationBannerService";

const TopBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
  max-width: 640px;
`;

const NewButton = styled(Button)`
  width: auto;
  min-width: 130px;
`;

const Card = styled.div`
  background: #fafafa;
  border: 1px solid #ebeeef;
  border-radius: 8px;
  padding: 20px 24px;
  margin-bottom: 16px;
  max-width: 640px;
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
`;

const StatusBadge = styled.span<{ published: boolean }>`
  font-family: Nunito Sans, sans-serif;
  font-size: 12px;
  font-weight: bold;
  padding: 3px 10px;
  border-radius: 999px;
  color: ${({ published }) => (published ? "#1f7a1f" : "#7a7f82")};
  background: ${({ published }) => (published ? "#e3f6e3" : "#ececec")};
`;

const FieldWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 16px;
`;

const Label = styled.div`
  font-family: Nunito Sans, sans-serif;
  font-size: 13px;
  font-weight: bold;
  color: #899093;
`;

const Select = styled.select`
  height: 44px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-family: Nunito Sans, sans-serif;
  font-size: 14px;
  padding: 0 12px;
  color: #494c5b;
  outline: none;
  background: white;
  width: 100%;
  max-width: 240px;
  box-sizing: border-box;
`;

const Preview = styled.div<{ bg: string; color: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  padding: 10px 16px;
  background: ${({ bg }) => bg};
  color: ${({ color }) => color};
  font-family: Nunito Sans, sans-serif;
  font-size: 14px;
  font-weight: 600;
  text-align: center;
  min-height: 20px;
`;

const Actions = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 4px;
  flex-wrap: wrap;
`;

const ActionButton = styled(Button)`
  width: auto;
  min-width: 110px;
`;

const DeleteButton = styled(ActionButton)`
  background: #fff;
  color: #e74c3c;
  border: 1px solid #e74c3c;
  & path {
    fill: #e74c3c;
  }
  :hover {
    background: #fdecea;
    color: #e74c3c;
    border: 1px solid #e74c3c;
  }
`;

const LoadingWrap = styled.div`
  display: flex;
  justify-content: center;
  padding: 48px 0;
`;

const EmptyState = styled.div`
  font-family: Nunito Sans, sans-serif;
  font-size: 14px;
  color: #899093;
  max-width: 640px;
  padding: 8px 0;
`;

const SEVERITIES: BannerSeverity[] = ["info", "warning", "error", "success"];

const AdminNotificationsTab: React.FC = () => {
  const [banners, setBanners] = useState<AdminBanner[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);

  const fetchBanners = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getNotificationBanners();
      setBanners(data.banners);
    } catch (e) {
      // toast already shown by api client
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBanners();
  }, [fetchBanners]);

  const patchLocal = (id: number, patch: Partial<AdminBanner>) =>
    setBanners((prev) => prev.map((b) => (b.id === id ? { ...b, ...patch } : b)));

  const handleCreate = async () => {
    setCreating(true);
    try {
      await createNotificationBanner();
      await fetchBanners();
    } catch (e) {
      // toast already shown
    } finally {
      setCreating(false);
    }
  };

  const persist = async (b: AdminBanner, enabled: boolean) => {
    if (enabled && !b.message.trim()) {
      toast.error("Add a message before publishing");
      return;
    }
    setBusyId(b.id);
    try {
      const res = await updateNotificationBanner(b.id, {
        message: b.message,
        severity: b.severity,
        enabled,
      });
      patchLocal(b.id, res.banner);
      toast.success(
        enabled === b.enabled
          ? "Saved"
          : enabled
          ? "Banner published"
          : "Banner unpublished"
      );
    } catch (e) {
      // toast already shown
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (b: AdminBanner) => {
    if (!window.confirm("Delete this banner?")) return;
    setBusyId(b.id);
    try {
      await deleteNotificationBanner(b.id);
      setBanners((prev) => prev.filter((x) => x.id !== b.id));
      toast.success("Banner deleted");
    } catch (e) {
      // toast already shown
    } finally {
      setBusyId(null);
    }
  };

  if (loading) {
    return (
      <LoadingWrap>
        <Spinner />
      </LoadingWrap>
    );
  }

  return (
    <div>
      <TopBar>
        <Text size={14} color="#899093">
          {banners.length} banner{banners.length === 1 ? "" : "s"} · published
          banners show at the top of the app for all users
        </Text>
        <NewButton onClick={handleCreate} disabled={creating}>
          {creating ? "Adding…" : "+ New banner"}
        </NewButton>
      </TopBar>

      {banners.length === 0 && (
        <EmptyState>
          No banners yet. Click “+ New banner”, write a message, pick a style, then
          Publish to show it to users.
        </EmptyState>
      )}

      {banners.map((b) => {
        const style = SEVERITY_STYLES[b.severity] ?? SEVERITY_STYLES.info;
        const busy = busyId === b.id;
        return (
          <Card key={b.id}>
            <CardHeader>
              <StatusBadge published={b.enabled}>
                {b.enabled ? "Published" : "Draft"}
              </StatusBadge>
            </CardHeader>

            <FieldWrap>
              <Label>Message</Label>
              <Input
                value={b.message}
                onChange={(e) => patchLocal(b.id, { message: e.target.value })}
                placeholder="e.g. Scheduled maintenance tonight 22:00–23:00"
                maxLength={300}
              />
            </FieldWrap>

            <FieldWrap>
              <Label>Style</Label>
              <Select
                value={b.severity}
                onChange={(e) =>
                  patchLocal(b.id, { severity: e.target.value as BannerSeverity })
                }
              >
                {SEVERITIES.map((s) => (
                  <option key={s} value={s}>
                    {SEVERITY_STYLES[s].label}
                  </option>
                ))}
              </Select>
            </FieldWrap>

            <FieldWrap>
              <Label>Preview</Label>
              <Preview bg={style.bg} color={style.text}>
                {b.message.trim() || "Banner preview"}
              </Preview>
            </FieldWrap>

            <Actions>
              <ActionButton
                kind="outline"
                onClick={() => persist(b, b.enabled)}
                disabled={busy}
              >
                Save
              </ActionButton>
              <ActionButton onClick={() => persist(b, !b.enabled)} disabled={busy}>
                {b.enabled ? "Unpublish" : "Publish"}
              </ActionButton>
              <DeleteButton onClick={() => handleDelete(b)} disabled={busy}>
                Delete
              </DeleteButton>
            </Actions>
          </Card>
        );
      })}
    </div>
  );
};

export default AdminNotificationsTab;
